/**
 * @fileoverview Input/output schemas for character-details-multimodal-generator-v3.
 *
 * This agent generates detailed attributes for a character using multimodal input.
 * It can accept images, text instructions, or both. Priority hierarchy:
 * 1. What is visible in provided images
 * 2. What the user's textual guidance specifies
 * 3. What already exists in the character's current attributes
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { CharacterAttributesSchema } from '@/domains/characters/schemas/character-sub-entities-v2';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 * Accepts optional images and/or text instructions. At least one must be provided.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.activeCharacterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.initialChapterContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.activeChapterContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chosenInspirations.schema.shape,
  /**
   * Optional array of image data URIs (base64 encoded).
   * Each should be in format: data:image/jpeg;base64,... or data:image/png;base64,...
   * Maximum 5 images allowed.
   */
  imageDataUris: z.array(z.string()).max(5).optional().describe(
    "Optional array of image data URIs (base64 encoded) to analyze. Must be images of a single person."
  ),
  /**
   * Optional user-provided text instructions to guide generation.
   * Examples: "Make them a mysterious wanderer", "Like the image, but with curly hair"
   */
  userMessage: z.string().optional().describe(
    "Optional user-provided text instructions to guide the generation."
  ),
  /**
   * The name of the character being created (for context).
   */
  characterName: z.string().describe("The name of the character being created."),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output schema includes validation metadata and full character attributes.
 */
export const OutputSchema = z.object({
  /**
   * Whether the image validation passed (if images were provided).
   * False if: multiple people detected, no person detected, or non-person subject.
   * Always true if no images were provided.
   */
  isValidImage: z.boolean().describe(
    "True if no images were provided OR if images contain exactly one person. False if image validation failed."
  ),
  /**
   * Error message if validation failed.
   */
  validationError: z.string().optional().describe(
    "Explanation of why the image is invalid, if isValidImage is false."
  ),
  /**
   * The generated character attributes. Only present if isValidImage is true.
   */
  attributes: CharacterAttributesSchema.optional().describe(
    "The generated character attributes. Only present if isValidImage is true."
  ),
});

export type Output = z.infer<typeof OutputSchema>;
